import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { adminAPI, resolveImg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Users, Package, Gavel, Euro, Repeat, MessageCircle, Trash2, Star, Shield, ChevronRight, Crown, Activity, TrendingUp, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [offers, setOffers] = useState([]);
  const [barters, setBarters] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [openConv, setOpenConv] = useState(null);
  const [convMessages, setConvMessages] = useState([]);
  const [lomNote, setLomNote] = useState("");
  const [lomTarget, setLomTarget] = useState(null);
  const [verifications, setVerifications] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    if (!user.is_admin) {
      toast.error("Accesso riservato agli amministratori");
      navigate("/");
      return;
    }
    refresh();
  }, [user, authLoading, navigate]);

  const refresh = async () => {
    try {
      const [s, u, l, a, o, b, c, v] = await Promise.all([
        adminAPI.stats(), adminAPI.users(), adminAPI.listings(),
        adminAPI.auctions(), adminAPI.offers(), adminAPI.barters(),
        adminAPI.conversations(), adminAPI.verifications(),
      ]);
      setStats(s); setUsers(u); setListings(l);
      setAuctions(a); setOffers(o); setBarters(b); setConversations(c);
      setVerifications(v);
    } catch (e) {
      toast.error("Errore caricamento dati admin");
    }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Eliminare questo utente e tutti i suoi annunci?")) return;
    await adminAPI.deleteUser(uid);
    toast.success("Utente eliminato");
    refresh();
  };

  const deleteListing = async (lid) => {
    if (!window.confirm("Eliminare questo annuncio?")) return;
    await adminAPI.deleteListing(lid);
    toast.success("Annuncio eliminato");
    refresh();
  };

  const toggleFeature = async (lid) => {
    await adminAPI.toggleFeature(lid);
    refresh();
  };

  const cleanupDemo = async () => {
    const ok1 = window.confirm(
      "Eliminare TUTTI gli annunci, aste, offerte, baratti e messaggi creati dagli utenti demo (marco, giulia, luca, anna, davide)?\n\nQuesta operazione NON può essere annullata."
    );
    if (!ok1) return;
    const alsoUsers = window.confirm(
      "Vuoi anche eliminare gli utenti demo (marco@, giulia@, luca@, anna@, davide@)?\n\nOK = elimina anche gli utenti\nAnnulla = mantieni solo gli utenti, cancella solo i loro contenuti"
    );
    try {
      const r = await adminAPI.cleanupDemo(alsoUsers);
      const x = r.removed || {};
      toast.success("Dati demo eliminati", {
        description: `Annunci: ${x.listings || 0} · Aste: ${x.auctions || 0} · Offerte: ${x.offers || 0} · Baratti: ${x.barters || 0} · Messaggi: ${x.messages || 0}${alsoUsers ? ` · Utenti: ${x.users || 0}` : ""}`,
      });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cleanup");
    }
  };

  const setListingOfMonth = async () => {
    if (!lomTarget) return;
    try {
      await adminAPI.setListingOfMonth(lomTarget.id, lomNote.trim());
      toast.success("Annuncio del Mese impostato", { description: lomTarget.title });
      setLomTarget(null);
      setLomNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const clearListingOfMonth = async () => {
    if (!window.confirm("Rimuovere l'annuncio del mese?")) return;
    try {
      await adminAPI.clearListingOfMonth();
      toast.success("Annuncio del Mese rimosso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const approveVerification = async (uid) => {
    if (!window.confirm("Approvare la verifica identità? Il documento sarà eliminato dai server dopo l'approvazione (GDPR).")) return;
    try {
      await adminAPI.approveVerification(uid);
      toast.success("Verifica approvata. Documento eliminato.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const rejectVerification = async (uid) => {
    const reason = window.prompt("Motivo del rifiuto (verrà inviato all'utente):", "Documento illeggibile");
    if (!reason || !reason.trim()) return;
    try {
      await adminAPI.rejectVerification(uid, reason.trim());
      toast.success("Verifica rifiutata. Documento eliminato.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const openConversation = async (conv) => {
    setOpenConv(conv);
    const msgs = await adminAPI.convMessages(conv.id);
    setConvMessages(msgs);
  };

  if (authLoading || !user) return <div className="p-16 text-center text-slate-500">Caricamento...</div>;
  if (!user.is_admin) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="bg-gradient-to-r from-[#1d3d3a] to-[#2d5350] rounded-2xl p-6 text-white mb-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#e86a24] flex items-center justify-center">
            <Crown className="w-6 h-6"/>
          </div>
          <div>
            <h1 className="text-2xl font-black">Pannello Amministratore</h1>
            <p className="text-sm text-slate-300">Gestione completa SAN MARINO MARKET</p>
          </div>
        </div>
        <Button onClick={refresh} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
          <Activity className="w-4 h-4 mr-2"/>Aggiorna
        </Button>
      </div>

      {/* Cleanup demo data */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-2xl p-4 mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#e86a24] text-white flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-black text-[#1d3d3a]">Pulisci dati demo</div>
            <div className="text-xs text-slate-600">
              Cancella in blocco annunci, aste, offerte, baratti e messaggi creati dagli utenti demo (Marco, Giulia, Luca, Anna, Davide). Lascia intatti gli utenti reali.
            </div>
          </div>
        </div>
        <Button onClick={cleanupDemo} className="bg-[#e86a24] hover:bg-[#c8561a] rounded-full" data-testid="admin-cleanup-demo-button">
          <Trash2 className="w-4 h-4 mr-2" /> Elimina dati demo
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard icon={Users} label="Utenti" value={stats.users} color="bg-blue-100 text-blue-700"/>
          <StatCard icon={Package} label="Annunci" value={stats.listings} subLabel={`${stats.listings_featured} vetrine`} color="bg-orange-100 text-[#e86a24]"/>
          <StatCard icon={Gavel} label="Aste" value={stats.auctions} subLabel={`${stats.auctions_active} attive`} color="bg-amber-100 text-amber-700"/>
          <StatCard icon={Euro} label="Offerte" value={stats.offers} color="bg-emerald-100 text-emerald-700"/>
          <StatCard icon={Repeat} label="Baratti" value={stats.barters} color="bg-teal-100 text-teal-700"/>
          <StatCard icon={MessageCircle} label="Conversazioni" value={stats.conversations} color="bg-purple-100 text-purple-700"/>
          <StatCard icon={Zap} label="Messaggi" value={stats.messages} color="bg-pink-100 text-pink-700"/>
          <StatCard icon={TrendingUp} label="Bids" value={stats.bids} color="bg-indigo-100 text-indigo-700"/>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="stats">Panoramica</TabsTrigger>
          <TabsTrigger value="users">Utenti</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="admin-tab-verifications">Verifiche</TabsTrigger>
          <TabsTrigger value="listings">Annunci</TabsTrigger>
          <TabsTrigger value="auctions">Aste</TabsTrigger>
          <TabsTrigger value="offers">Offerte</TabsTrigger>
          <TabsTrigger value="barters">Baratti</TabsTrigger>
          <TabsTrigger value="chat">Chat</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="admin-tab-transactions">€ Transazioni</TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4">Panoramica sistema</h3>
            <p className="text-sm text-slate-600">Sistema attivo. Tutte le metriche sono mostrate nei riquadri sopra. Usa i tab per navigare tra le diverse sezioni.</p>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-3">Utente</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Castello</th>
                  <th className="text-left p-3">Annunci</th>
                  <th className="text-left p-3">Rating</th>
                  <th className="text-left p-3">Iscritto</th>
                  <th className="text-right p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 flex items-center gap-2">
                      <img src={u.avatar} alt="" className="w-8 h-8 rounded-full"/>
                      <span className="font-semibold">{u.name}</span>
                      {u.is_admin && <Badge className="bg-[#e86a24] text-[10px]"><Shield className="w-3 h-3 mr-0.5"/>ADMIN</Badge>}
                    </td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3">{u.castello}</td>
                    <td className="p-3 font-bold">{u.listings_count}</td>
                    <td className="p-3">⭐ {Number(u.rating || 5).toFixed(1)}</td>
                    <td className="p-3 text-xs text-slate-500">{new Date(u.joined_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-3 text-right">
                      {!u.is_admin && (
                        <button onClick={() => deleteUser(u.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600">
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="verifications" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-black text-[#1d3d3a] mb-1 flex items-center gap-2">
              <Shield className="w-5 h-5 text-sky-600"/>
              Verifiche identità in coda ({verifications.length})
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              ⚠️ <strong>GDPR:</strong> i documenti vengono <strong>cancellati automaticamente</strong> dai server subito dopo approvazione o rifiuto. Non scaricarli localmente.
            </p>
            {verifications.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">Nessuna verifica in attesa</div>
            ) : (
              <div className="space-y-3">
                {verifications.map(v => {
                  const docUrl = v.verification_doc_url || "";
                  const isPdf = docUrl.toLowerCase().endsWith(".pdf");
                  return (
                  <div key={v.id} className="border border-slate-200 rounded-xl overflow-hidden flex flex-col md:flex-row gap-3" data-testid={`verification-row-${v.id}`}>
                    {docUrl && (
                      <a href={resolveImg(docUrl)} target="_blank" rel="noopener noreferrer" className="md:w-56 shrink-0 bg-slate-100 block">
                        {isPdf ? (
                          <div className="w-full h-44 md:h-full flex flex-col items-center justify-center bg-red-50 hover:bg-red-100 transition-colors text-red-700 p-4">
                            <Shield className="w-10 h-10 mb-2"/>
                            <div className="font-bold text-sm">PDF documento</div>
                            <div className="text-xs underline mt-1">Apri per visualizzare</div>
                          </div>
                        ) : (
                          <img src={resolveImg(docUrl)} alt="documento" className="w-full h-44 md:h-full object-cover hover:opacity-90 transition-opacity" />
                        )}
                      </a>
                    )}
                    <div className="flex-1 min-w-0 p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <img src={v.avatar} alt="" className="w-8 h-8 rounded-full" />
                        <div className="font-bold text-[#1d3d3a]">{v.name}</div>
                        <Badge variant="secondary" className="text-[10px]">{v.castello}</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{v.email}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Tipo doc: <strong>{v.verification_doc_type || "id_card"}</strong> · Formato: <strong>{isPdf ? "PDF" : "Immagine"}</strong> · Inviato: {v.verification_submitted_at ? new Date(v.verification_submitted_at).toLocaleString("it-IT") : "—"}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button onClick={() => approveVerification(v.id)} className="bg-sky-600 hover:bg-sky-700 text-white rounded-full" size="sm" data-testid={`verification-approve-${v.id}`}>
                          ✓ Approva
                        </Button>
                        <Button onClick={() => rejectVerification(v.id)} variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50" size="sm" data-testid={`verification-reject-${v.id}`}>
                          ✕ Rifiuta
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="listings" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button onClick={clearListingOfMonth} variant="outline" size="sm" className="rounded-full text-xs" data-testid="admin-clear-lom-button">
              <Crown className="w-3.5 h-3.5 mr-1 text-amber-500" /> Rimuovi Annuncio del Mese
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-3">Annuncio</th>
                  <th className="text-left p-3">Prezzo</th>
                  <th className="text-left p-3">Venditore</th>
                  <th className="text-left p-3">Castello</th>
                  <th className="text-left p-3">Views</th>
                  <th className="text-left p-3">Stato</th>
                  <th className="text-right p-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {listings.map(l => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 flex items-center gap-2">
                      <img src={resolveImg(l.images?.[0])} alt="" className="w-10 h-10 rounded object-cover"/>
                      <span className="font-semibold truncate max-w-[200px]">{l.title}</span>
                    </td>
                    <td className="p-3 font-bold text-[#e86a24]">€ {Number(l.price).toLocaleString("it-IT")}</td>
                    <td className="p-3">{l.user?.name}</td>
                    <td className="p-3 text-slate-600">{l.castello}</td>
                    <td className="p-3">{l.views}</td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {l.featured && <Badge className="bg-amber-500 text-[10px]"><Star className="w-2.5 h-2.5 mr-0.5 fill-white"/>VETRINA</Badge>}
                        {l.allow_barter && <Badge variant="secondary" className="text-[10px]">Baratto</Badge>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => setLomTarget(l)} className="p-1.5 rounded hover:bg-amber-50 text-amber-700 mr-1" title="Imposta come Annuncio del Mese" data-testid={`admin-lom-${l.id}`}>
                        <Crown className="w-4 h-4"/>
                      </button>
                      <button onClick={() => toggleFeature(l.id)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600 mr-1" title="Toggle vetrina">
                        <Star className="w-4 h-4"/>
                      </button>
                      <button onClick={() => deleteListing(l.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="auctions" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-3">Titolo</th>
                  <th className="text-left p-3">Prezzo iniziale</th>
                  <th className="text-left p-3">Offerta attuale</th>
                  <th className="text-left p-3">Bids</th>
                  <th className="text-left p-3">Top bidder</th>
                  <th className="text-left p-3">Scade</th>
                </tr>
              </thead>
              <tbody>
                {auctions.map(a => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="p-3 font-semibold">{a.title}</td>
                    <td className="p-3">€ {Number(a.start_price).toLocaleString("it-IT")}</td>
                    <td className="p-3 font-bold text-[#e86a24]">€ {Number(a.current_bid).toLocaleString("it-IT")}</td>
                    <td className="p-3">{a.bid_count}</td>
                    <td className="p-3">{a.top_bidder || "-"}</td>
                    <td className="p-3 text-xs">{new Date(a.ends_at).toLocaleString("it-IT")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="offers" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-3">Annuncio</th>
                  <th className="text-left p-3">Da</th>
                  <th className="text-left p-3">Importo</th>
                  <th className="text-left p-3">Messaggio</th>
                  <th className="text-left p-3">Pagamento</th>
                  <th className="text-left p-3">Stato</th>
                  <th className="text-left p-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {offers.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Nessuna offerta</td></tr>}
                {offers.map(o => (
                  <tr key={o.id} className="border-t border-slate-100">
                    <td className="p-3 truncate max-w-[180px]">{o.listing_title}</td>
                    <td className="p-3">{o.buyer_name}</td>
                    <td className="p-3 font-bold text-[#e86a24]">€ {o.amount}</td>
                    <td className="p-3 text-xs text-slate-600 truncate max-w-[200px]">{o.message || "-"}</td>
                    <td className="p-3">{o.with_payment ? <Badge className="bg-emerald-600">Stripe</Badge> : <span className="text-slate-400">-</span>}</td>
                    <td className="p-3"><Badge variant="secondary">{o.status}</Badge></td>
                    <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="barters" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-3">Annuncio</th>
                  <th className="text-left p-3">Da</th>
                  <th className="text-left p-3">Oggetto offerto</th>
                  <th className="text-left p-3">Valore</th>
                  <th className="text-left p-3">+ €</th>
                  <th className="text-left p-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {barters.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Nessun baratto</td></tr>}
                {barters.map(b => (
                  <tr key={b.id} className="border-t border-slate-100">
                    <td className="p-3 truncate max-w-[180px]">{b.listing_title}</td>
                    <td className="p-3">{b.buyer_name}</td>
                    <td className="p-3 font-semibold">{b.item_title}</td>
                    <td className="p-3">€ {b.item_value}</td>
                    <td className="p-3">{b.add_cash ? <span className="font-bold text-emerald-700">+ € {b.cash_amount}</span> : "-"}</td>
                    <td className="p-3"><Badge variant="secondary">{b.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="chat" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 p-3 text-xs text-amber-900">
              <strong>Vista admin:</strong> i messaggi sono mostrati come visti dagli utenti (già censurati). I contenuti originali non vengono salvati.
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th className="text-left p-3">Partecipanti</th>
                  <th className="text-left p-3">Annuncio</th>
                  <th className="text-left p-3">Messaggi</th>
                  <th className="text-left p-3">Ultimo msg</th>
                  <th className="text-left p-3">Preview</th>
                  <th className="text-right p-3"></th>
                </tr>
              </thead>
              <tbody>
                {conversations.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Nessuna conversazione</td></tr>}
                {conversations.map(c => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openConversation(c)}>
                    <td className="p-3 font-semibold">{(c.participants_names || []).join(" ↔ ")}</td>
                    <td className="p-3 truncate max-w-[200px] text-slate-600">{c.listing_title}</td>
                    <td className="p-3"><Badge variant="secondary">{c.messages_count}</Badge></td>
                    <td className="p-3 text-xs">{new Date(c.last_message_at).toLocaleString("it-IT")}</td>
                    <td className="p-3 text-xs truncate max-w-[240px] text-slate-600">{c.last_message_preview || "-"}</td>
                    <td className="p-3 text-right"><ChevronRight className="w-4 h-4 text-slate-400 inline"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Dialog open={!!openConv} onOpenChange={() => { setOpenConv(null); setConvMessages([]); }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Conversazione: {openConv?.participants_names?.join(" ↔ ")}</DialogTitle>
                <DialogDescription>{openConv?.listing_title}</DialogDescription>
              </DialogHeader>
              <div className="overflow-y-auto space-y-2 p-2">
                {convMessages.length === 0 && <div className="text-center text-slate-400 py-8">Nessun messaggio</div>}
                {convMessages.map(m => (
                  <div key={m.id} className="border border-slate-200 rounded-lg p-3">
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="font-bold text-slate-700">{m.sender_name}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px]">{m.type}</Badge>
                        {m.censored && <Badge className="bg-amber-500 text-[10px]">Censurato</Badge>}
                        <span className="text-slate-400">{new Date(m.created_at).toLocaleString("it-IT")}</span>
                      </div>
                    </div>
                    {m.type === "text" && <div className="text-sm text-slate-800">{m.text}</div>}
                    {m.type === "image" && <img src={resolveImg(m.image_url)} alt="" className="max-w-xs rounded mt-1"/>}
                    {m.type === "location" && (
                      <div className="text-sm text-slate-800">
                        📍 {m.location?.label || "Posizione"} · <span className="font-mono text-xs">{m.location?.lat?.toFixed(5)}, {m.location?.lng?.toFixed(5)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <TransactionsPanel />
        </TabsContent>
      </Tabs>

      {/* Listing of the Month dialog */}
      <Dialog open={!!lomTarget} onOpenChange={(o) => { if (!o) { setLomTarget(null); setLomNote(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500"/>Annuncio del Mese</DialogTitle>
            <DialogDescription>Promuovi questo annuncio in evidenza nella home page con una nota della redazione.</DialogDescription>
          </DialogHeader>
          {lomTarget && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <img src={resolveImg(lomTarget.images?.[0])} alt="" className="w-14 h-14 rounded-lg object-cover"/>
                <div className="min-w-0">
                  <div className="font-bold truncate">{lomTarget.title}</div>
                  <div className="text-xs text-slate-500">€ {lomTarget.price} · {lomTarget.castello}</div>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 mb-1 block">Nota della redazione (opzionale, max 280 caratteri)</label>
                <textarea
                  value={lomNote}
                  onChange={(e) => setLomNote(e.target.value.slice(0, 280))}
                  placeholder='Es. "Un pezzo unico che racconta la storia artigianale di San Marino..."'
                  className="w-full min-h-[90px] border border-slate-300 rounded-lg p-2 text-sm resize-none focus:ring-2 focus:ring-[#e86a24] focus:border-transparent"
                  data-testid="admin-lom-note"
                />
                <div className="text-[10px] text-slate-400 text-right">{lomNote.length}/280</div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setLomTarget(null); setLomNote(""); }}>Annulla</Button>
                <Button onClick={setListingOfMonth} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="admin-lom-confirm">
                  <Crown className="w-4 h-4 mr-1"/> Imposta in home
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subLabel, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center mb-2`}>
        <Icon className="w-5 h-5"/>
      </div>
      <div className="text-2xl font-black text-[#1d3d3a]">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
      {subLabel && <div className="text-[10px] text-slate-400 mt-0.5">{subLabel}</div>}
    </div>
  );
}

function TransactionsPanel() {
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [status, setStatus] = useState("all");
  const [pkgFilter, setPkgFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        adminAPI.transactionsSummary(days),
        adminAPI.transactions({ days, status, package_id: pkgFilter }),
      ]);
      setSummary(s);
      setTxs(t);
    } catch (e) {
      toast.error("Errore caricamento transazioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days, status, pkgFilter]); // eslint-disable-line

  const exportCSV = () => {
    const headers = ["Data", "Utente", "Email", "Tipo Account", "Paese", "Pacchetto", "Importo", "Stato Pagamento", "Annuncio", "Session ID"];
    const rows = txs.map((t) => [
      t.created_at ? new Date(t.created_at).toLocaleString("it-IT") : "",
      t.user_name || "",
      t.user_email || "",
      t.account_type || "",
      t.country || "",
      t.package_label || "",
      Number(t.amount || 0).toFixed(2),
      t.payment_status || "",
      t.listing_title || t.listing_id || "",
      t.session_id || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transazioni-smm-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-transactions-panel">
      {/* Filtri */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div>
          <label className="text-[10px] font-black uppercase text-slate-500 block mb-1">Periodo</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold">
            <option value="7">Ultimi 7 giorni</option>
            <option value="30">Ultimi 30 giorni</option>
            <option value="90">Ultimi 90 giorni</option>
            <option value="365">Ultimo anno</option>
            <option value="9999">Tutto</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase text-slate-500 block mb-1">Stato</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold">
            <option value="all">Tutti</option>
            <option value="paid">Pagati</option>
            <option value="pending">In attesa</option>
            <option value="failed">Falliti</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] font-black uppercase text-slate-500 block mb-1">Pacchetto</label>
          <select value={pkgFilter} onChange={(e) => setPkgFilter(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold">
            <option value="all">Tutti i pacchetti</option>
            <option value="vetrina_7d">Vetrina 7gg (€4,99)</option>
            <option value="vetrina_30d">Vetrina 30gg (€14,99)</option>
            <option value="business_subscription_year">Iscrizione Azienda SM (€9,99)</option>
            <option value="business_subscription_year_it">Iscrizione Azienda IT (€29,99)</option>
            <option value="business_listing_fee">Annuncio Azienda SM (€0,50)</option>
            <option value="business_listing_fee_it">Annuncio Azienda IT (€1)</option>
            <option value="auto_listing_monthly">Annuncio Auto (€1)</option>
          </select>
        </div>
        <Button onClick={exportCSV} variant="outline" className="rounded-lg mt-4 ml-auto" data-testid="admin-tx-export">
          📥 Esporta CSV
        </Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryStatCard label="Incasso totale" value={`€ ${summary.grand_total.toFixed(2)}`} sub={`${summary.grand_count} transazioni pagate`} color="bg-emerald-100 text-emerald-700" />
          <SummaryStatCard label="In attesa" value={summary.by_status?.pending || 0} sub="Checkout iniziati non pagati" color="bg-amber-100 text-amber-700" />
          <SummaryStatCard label="Pagati" value={summary.by_status?.paid || 0} sub="Completati con successo" color="bg-blue-100 text-blue-700" />
          <SummaryStatCard label="Falliti" value={summary.by_status?.failed || 0} sub="Pagamento rifiutato" color="bg-rose-100 text-rose-700" />
        </div>
      )}

      {/* Per pacchetto */}
      {summary?.by_package?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200 font-black text-[#1d3d3a]">Incassi per tipo di servizio</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">Servizio</th>
                <th className="text-right p-3">N. acquisti</th>
                <th className="text-right p-3">Incasso totale</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_package.map((p) => (
                <tr key={p.package_id} className="border-t border-slate-100">
                  <td className="p-3 font-semibold">{p.label}</td>
                  <td className="p-3 text-right">{p.count}</td>
                  <td className="p-3 text-right font-black text-emerald-700">€ {p.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lista transazioni */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-black text-[#1d3d3a]">Elenco transazioni Stripe</div>
          <div className="text-xs text-slate-500">{txs.length} risultati (max 500)</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Caricamento…</div>
        ) : txs.length === 0 ? (
          <div className="p-10 text-center text-slate-500">Nessuna transazione nel periodo selezionato</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-3">Data</th>
                  <th className="text-left p-3">Utente</th>
                  <th className="text-left p-3">Servizio</th>
                  <th className="text-left p-3">Annuncio</th>
                  <th className="text-right p-3">Importo</th>
                  <th className="text-center p-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tx-row-${t.id}`}>
                    <td className="p-3 text-xs text-slate-600">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString("it-IT") : "—"}
                      <div className="text-[10px] text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-bold text-slate-800">{t.user_name || "—"}</div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1">
                        {t.user_email}
                        {t.account_type === "business" && (
                          <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-800 px-1 py-0">{t.country === "IT" ? "🇮🇹 BIZ" : "🇸🇲 BIZ"}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-xs">{t.package_label}</td>
                    <td className="p-3 text-xs text-slate-500 max-w-[160px] truncate">{t.listing_title || (t.listing_id ? t.listing_id.slice(0, 8) + "…" : "—")}</td>
                    <td className="p-3 text-right font-black text-emerald-700">€ {Number(t.amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-center">
                      <Badge className={`text-[10px] ${
                        t.payment_status === "paid" ? "bg-emerald-600" :
                        t.payment_status === "pending" ? "bg-amber-500" :
                        "bg-rose-500"
                      } text-white`}>
                        {t.payment_status === "paid" ? "PAGATO" : t.payment_status === "pending" ? "ATTESA" : (t.payment_status || "—").toUpperCase()}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top users */}
      {summary?.top_users?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200 font-black text-[#1d3d3a]">Top 10 utenti per spesa</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">Utente</th>
                <th className="text-left p-3">Tipo</th>
                <th className="text-right p-3">Acquisti</th>
                <th className="text-right p-3">Speso totale</th>
              </tr>
            </thead>
            <tbody>
              {summary.top_users.map((u) => (
                <tr key={u.user_id} className="border-t border-slate-100">
                  <td className="p-3 font-bold">{u.user_name}<div className="text-[10px] text-slate-500">{u.user_email}</div></td>
                  <td className="p-3 text-xs">{u.account_type === "business" ? "Azienda" : "Privato"}</td>
                  <td className="p-3 text-right">{u.count}</td>
                  <td className="p-3 text-right font-black text-emerald-700">€ {u.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryStatCard({ label, value, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase ${color} mb-2`}>{label}</div>
      <div className="text-2xl font-black text-[#1d3d3a]">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

